import { z } from "zod";

/**
 * Shared DTO cho Admin Usage Metering & Unit Economics (US-AE14.F2-0047 · @UC9131 usage-metering ·
 * @UC9133 unit-economics). MỘT nguồn sự thật cho api (S01), worker cron (S06), admin web (S03).
 *
 * Read-only projection per nhóm/kỳ. Economics = doanh thu gói (Plan.priceMinor × sub, F1-0046) − chi
 * phí thực (token × đơn giá + hạ tầng). Đơn giá token + infra-cost = config PENDING (KHÔNG bịa).
 *
 * BẤT BIẾN (invariants):
 * - MONEY no-float: aumMinor/costMinor/revenueMinor/marginMinor = BigInt minor → STRING trên wire.
 * - AC (G2): metering KHÔNG tạo bút toán ledger — chỉ đọc/đếm. DTO KHÔNG có field ghi ledger.
 * - Đơn giá/infra = 0 khi PENDING (chưa cấu hình) — economics vẫn tính được (cost=0, margin=revenue).
 */

/** money minor trên wire = string chữ số (BigInt-safe). marginMinor có thể âm (chuỗi có dấu). */
const minorStringSchema = z.string().regex(/^-?\d+$/, "money minor phải chuỗi số nguyên (no-float)");
const unsignedMinorSchema = z.string().regex(/^\d+$/, "money minor phải chuỗi số nguyên không âm");

// ============================================================
// Usage meter (@UC9131) — 1 dòng metering per nhóm/kỳ
// ============================================================

export const usageMeterItemSchema = z
  .object({
    id: z.string(),
    groupId: z.string(),
    periodStart: z.string(),
    periodEnd: z.string(),
    txCount: z.number().int(),
    memberCount: z.number().int(),
    fundCount: z.number().int(),
    aumMinor: unsignedMinorSchema, // Σ số dư CASH (minor)
    aiTokens: z.string().regex(/^\d+$/), // BigInt token → string
    createdAt: z.string(),
  })
  .strict();

export type UsageMeterItem = z.infer<typeof usageMeterItemSchema>;

export const usageListResponseSchema = z
  .object({
    data: z.array(usageMeterItemSchema),
    pageInfo: z.object({ hasNext: z.boolean(), nextCursor: z.string().nullable() }).strict(),
  })
  .strict();

export type UsageListResponse = z.infer<typeof usageListResponseSchema>;

export const USAGE_LIST_MAX_LIMIT = 100;

export const usageListQuerySchema = z
  .object({
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(USAGE_LIST_MAX_LIMIT).optional(),
  })
  .strict();

export type UsageListQuery = z.infer<typeof usageListQuerySchema>;

// ============================================================
// Unit economics (@UC9133) — cost vs revenue vs margin per nhóm
// ============================================================

/**
 * 1 dòng unit-economics per nhóm (kỳ hiện tại). costMinor = tokens×đơn giá + infra (0 khi PENDING);
 * revenueMinor = Plan.priceMinor gói nhóm (0 nếu không sub); marginMinor = revenue − cost (có thể âm).
 * pending = true nếu đơn giá/infra chưa cấu hình (economics chưa đủ tin — hiển thị cảnh báo).
 */
export const unitEconomicsItemSchema = z
  .object({
    groupId: z.string(),
    planCode: z.string().nullable(),
    aiTokens: z.string().regex(/^\d+$/),
    costMinor: unsignedMinorSchema,
    revenueMinor: unsignedMinorSchema,
    marginMinor: minorStringSchema, // có thể âm
    pending: z.boolean(), // đơn giá/infra chưa cấu hình → economics = placeholder
  })
  .strict();

export type UnitEconomicsItem = z.infer<typeof unitEconomicsItemSchema>;

export const unitEconomicsResponseSchema = z
  .object({
    data: z.array(unitEconomicsItemSchema),
    pageInfo: z.object({ hasNext: z.boolean(), nextCursor: z.string().nullable() }).strict(),
  })
  .strict();

export type UnitEconomicsResponse = z.infer<typeof unitEconomicsResponseSchema>;

// ============================================================
// Config keys (đơn giá token + infra-cost) — config-driven, default 0 PENDING (KHÔNG bịa)
// ============================================================

export const ECONOMICS_KEYS = {
  tokenUnitPriceMinor: "billing.token_unit_price_minor", // minor VND / 1 token (0 = PENDING)
  infraCostPerGroupMinor: "billing.infra_cost_per_group_minor", // minor VND / nhóm / kỳ (0 = PENDING)
} as const;

export const ECONOMICS_DEFAULTS = {
  tokenUnitPriceMinor: "0", // PENDING — PO nhập sau (KHÔNG bịa đơn giá)
  infraCostPerGroupMinor: "0", // PENDING
} as const;
